/**
 * =============================================================================
 * PharmacyItemsTable Constants
 * =============================================================================
 *
 * Defines test IDs and ARIA labels for the Pharmacy Items Table component.
 * Following naming convention: <screen>-<component>-<intent>
 */

// -----------------------------------------------------------------------------
// Test ID Namespace
// -----------------------------------------------------------------------------
const SCREEN_PREFIX = 'pharmacy-items';

// -----------------------------------------------------------------------------
// Test IDs for Interactive Elements
// -----------------------------------------------------------------------------
export const TEST_IDS = {
  // Table Container
  tableContainer: `${SCREEN_PREFIX}-table-container`,
  emptyState: `${SCREEN_PREFIX}-empty-state`,

  // Table Structure
  table: `${SCREEN_PREFIX}-table`,
  tableHeader: `${SCREEN_PREFIX}-table-header`,
  tableBody: `${SCREEN_PREFIX}-table-body`,
} as const;

// -----------------------------------------------------------------------------
// ARIA Labels for Accessibility
// -----------------------------------------------------------------------------
export const ARIA_LABELS = {
  tableContainer: 'Pharmacy items table',
  emptyState: 'No pharmacy items added',
  itemCheckbox: (itemName: string) => `Select ${itemName}`,
  removeButton: (itemName: string) => `Remove ${itemName} from list`,
  batchCodeSelect: (itemName: string) => `Select batch code for ${itemName}`,
  billedUomSelect: (itemName: string) => `Select unit of measure for ${itemName}`,
  strengthInput: (itemName: string) => `Enter strength for ${itemName}`,
  quantityInput: (itemName: string) => `Enter quantity for ${itemName}`,
  concessionAmountInput: (itemName: string) => `Enter concession amount for ${itemName}`,
  concessionPercentInput: (itemName: string) => `Enter concession percentage for ${itemName}`,
  pregnancyClassSelect: (itemName: string) => `Select pregnancy class for ${itemName}`,
} as const;

// -----------------------------------------------------------------------------
// Helper function to generate item row test IDs
// -----------------------------------------------------------------------------
export const getItemRowTestIds = (itemCode: string) => {
  const safeCode = itemCode?.replace(/[^a-zA-Z0-9-]/g, '-') || 'unknown';
  return {
    row: `${SCREEN_PREFIX}-row-${safeCode}`,
    checkbox: `${SCREEN_PREFIX}-row-${safeCode}-checkbox`,
    batchSelect: `${SCREEN_PREFIX}-row-${safeCode}-batch-select`,
    strengthInput: `${SCREEN_PREFIX}-row-${safeCode}-strength-input`,
    qtyInput: `${SCREEN_PREFIX}-row-${safeCode}-qty-input`,
    uomSelect: `${SCREEN_PREFIX}-row-${safeCode}-uom-select`,
    concessionAmountInput: `${SCREEN_PREFIX}-row-${safeCode}-concession-amount`,
    concessionPercentInput: `${SCREEN_PREFIX}-row-${safeCode}-concession-percent`,
    pregnancyClassSelect: `${SCREEN_PREFIX}-row-${safeCode}-pregnancy-class`,
    removeButton: `${SCREEN_PREFIX}-row-${safeCode}-remove-btn`,
  };
};

// -----------------------------------------------------------------------------
// Pregnancy Classes
// -----------------------------------------------------------------------------
export const PREGNANCY_CLASSES = ['A', 'B', 'C', 'D', 'X'] as const;

// -----------------------------------------------------------------------------
// Table Scrollbar Styles (extracted for reusability)
// -----------------------------------------------------------------------------
export const TABLE_SCROLLBAR_STYLES = {
  maxHeight: { xs: 320, md: 480 },
  overflowX: 'auto',

  // ---------- Webkit (Chrome / Edge / Safari) ----------
  '&::-webkit-scrollbar': {
    height: 6,
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#E4E7EC',
    borderRadius: 8,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#D0D5DD',
      cursor: 'grab',
    },
    '&:active': {
      cursor: 'grabbing',
    },
  },

  // On container hover → bigger grab target, slightly more visible
  '&:hover::-webkit-scrollbar': {
    height: 12,
    width: 8,
  },
  '&:hover::-webkit-scrollbar-track': {
    backgroundColor: '#F9FAFB',
    cursor: 'pointer',
  },
  '&:hover::-webkit-scrollbar-thumb': {
    backgroundColor: '#D0D5DD',
    border: '2px solid #F9FAFB',
    '&:hover': {
      backgroundColor: '#BFC6CE',
      border: '2px solid #F9FAFB',
      cursor: 'grab',
    },
    '&:active': {
      backgroundColor: '#98A2B3',
      cursor: 'grabbing',
    },
  },

  // ---------- Firefox ----------
  scrollbarWidth: 'thin',
  scrollbarColor: '#E4E7EC transparent',
  '&:hover': {
    scrollbarColor: '#D0D5DD #F9FAFB',
  },
} as const;

// -----------------------------------------------------------------------------
// Table Header Row Styles
// -----------------------------------------------------------------------------
export const TABLE_HEADER_ROW_STYLES = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: '#F5F6F7',
  '& .MuiTableCell-root': { borderBottom: 'none', background: '#F5F6F7' },
  whiteSpace: 'nowrap',
} as const;

// -----------------------------------------------------------------------------
// Input Field Styles (shared across fields)
// -----------------------------------------------------------------------------
export const INPUT_FIELD_STYLES = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    '& fieldset': { borderColor: '#E4E7EC' },
    '&:hover fieldset': { borderColor: '#D0D5DD' },
    '&.Mui-focused fieldset': {
      borderColor: '#5A8AEA',
      boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
    },
  },
} as const;

export const SELECT_FIELD_STYLES = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: 3,
    borderColor: '#E4E7EC',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D0D5DD',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#5A8AEA',
    boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
  },
} as const;
